import React from 'react';
import {
    Box,
    Button,
    Flex,
    HStack,
    Icon,
    IconButton,
    Image,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Text,
    Tooltip,
    useToast,
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import { TbChevronDown, TbChevronUp, TbDownload, TbRefresh, TbRotateClockwise, TbSparkles, TbZoomIn, TbZoomOut } from 'react-icons/tb';
import ZoomImage, { ZoomImageHandle, type ZoomImageHighlightBox } from '../ZoomImage';
import PdfZoomImage from '../PdfZoomImage';
import InvoicePageDraftStrip from './InvoicePageDraftStrip';
import { downloadImagesAsPdf, downloadPdfFromSource, renderPdfPageDataUrlThumbnails } from '../../utils/invoicePdfPages';
import { INVOICE_IMAGE_ADD_ACCEPT } from '../../utils/invoiceUploadFiles';

const ACCENT = '#0071E3';

export interface InvoicePageReplacementControls {
    show: boolean;
    editMode: boolean;
    draftBootstrapping: boolean;
    isPdf: boolean;
    pdfThumbsLoading: boolean;
    thumbnailUrls: string[];
    sortableIds: string[];
    selectedIndex0: number;
    onSelectIndex: (index0: number) => void;
    onReorder: (fromIndex0: number, toIndex0: number) => void;
    onRemoveAt: (index0: number) => void;
    onAddImages: (files: File[]) => void;
    imageAddAccept?: string;
    onAddPdf: (file: File) => void;
    onReprocess: () => void;
    applyLoading: boolean;
    error: string | null;
    hasPages: boolean;
    onCancel: () => void;
    onStartOver: () => void;
    panelExpanded: boolean;
    onSetPanelExpanded: (expanded: boolean) => void;
}

export interface InvoiceImagePanelPageEditActions {
    visible: boolean;
    hasPages: boolean;
    applyLoading: boolean;
    onAdjustImages: () => void;
    onResetOrder: () => void;
    onRemoveAll: () => void;
    onReprocess: () => void;
}

export interface InvoiceImagePanelProps {
    panelWidthPercent?: number;
    currentImageSrc: string | null;
    isPdf: boolean;
    allImages: string[];
    currentImagePage: number;
    onPageChange: (page: number) => void;
    zoomImageRef: React.RefObject<ZoomImageHandle>;
    invoiceNumber?: string;
    /** Hover highlight on the current page image (JPEG/PNG and PDF). */
    imageHighlight?: ZoomImageHighlightBox | null;
    imageHighlightFullWidth?: boolean;
    teachingOpen?: boolean;
    onOpenTeaching?: () => void;
    onPdfPageCountChange?: (count: number) => void;
    pdfPageCount?: number;
    pageReplacement?: InvoicePageReplacementControls;
    onZoomOut?: () => void;
    onZoomIn?: () => void;
    onZoomReset?: () => void;
    onRotate?: () => void;
    /** Edit/Re-process actions rendered inside the floating glass toolbar (only when `visible`). */
    pageEditActions?: InvoiceImagePanelPageEditActions;
}

const InvoiceImagePanel = ({
    panelWidthPercent = 37,
    currentImageSrc,
    isPdf,
    allImages,
    currentImagePage,
    onPageChange,
    zoomImageRef,
    invoiceNumber,
    imageHighlight,
    imageHighlightFullWidth = false,
    teachingOpen = false,
    onOpenTeaching,
    onPdfPageCountChange,
    pdfPageCount,
    pageReplacement,
    onZoomOut,
    onZoomIn,
    onZoomReset,
    onRotate,
    pageEditActions,
}: InvoiceImagePanelProps) => {
    const toast = useToast();
    const pdfAppendInputRef = React.useRef<HTMLInputElement>(null);
    const imageAppendInputRef = React.useRef<HTMLInputElement>(null);
    const [pdfViewThumbUrls, setPdfViewThumbUrls] = React.useState<string[]>([]);
    const [isDownloadingPdf, setIsDownloadingPdf] = React.useState(false);

    const hideDefaultThumbnailStrip = !!(pageReplacement?.editMode || pageReplacement?.draftBootstrapping);

    const handleDownloadPdf = React.useCallback(async () => {
        const filenameBase = invoiceNumber?.trim()
            ? `invoice-${invoiceNumber.trim()}`
            : 'invoice';
        setIsDownloadingPdf(true);
        try {
            if (isPdf) {
                if (!currentImageSrc) {
                    throw new Error('Invoice PDF is not available.');
                }
                await downloadPdfFromSource(currentImageSrc, filenameBase);
            } else {
                const pages = allImages.length > 0
                    ? allImages
                    : currentImageSrc
                      ? [currentImageSrc]
                      : [];
                await downloadImagesAsPdf(pages, filenameBase);
            }
        } catch (err) {
            toast({
                title: 'Could not download PDF',
                description: err instanceof Error ? err.message : 'Please try again.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setIsDownloadingPdf(false);
        }
    }, [allImages, currentImageSrc, invoiceNumber, isPdf, toast]);

    React.useEffect(() => {
        if (!isPdf || !currentImageSrc || hideDefaultThumbnailStrip || (pdfPageCount ?? 1) <= 1) {
            setPdfViewThumbUrls([]);
            return;
        }
        let cancelled = false;
        renderPdfPageDataUrlThumbnails(currentImageSrc)
            .then((thumbs) => {
                if (!cancelled) setPdfViewThumbUrls(thumbs);
            })
            .catch(() => {
                if (!cancelled) setPdfViewThumbUrls([]);
            });
        return () => {
            cancelled = true;
        };
    }, [isPdf, currentImageSrc, pdfPageCount, hideDefaultThumbnailStrip]);

    return (
        <Flex
            w={`${panelWidthPercent}%`}
            h="100%"
            direction="column"
            flexShrink={0}
            borderRight="1px solid"
            borderColor="gray.200"
            bg="gray.50"
        >
            {/* Image area */}
            <Box flex="1" overflow="hidden" position="relative">
                {/* Liquid-glass floating toolbar */}
                <HStack
                    position="absolute"
                    bottom={3}
                    left="50%"
                    transform="translateX(-50%)"
                    zIndex={2}
                    align="center"
                    spacing={3}
                    px={3}
                    py={2}
                    borderRadius="xl"
                    border="1px solid"
                    borderColor="whiteAlpha.600"
                    bg="rgba(255, 255, 255, 0.55)"
                    sx={{
                        backdropFilter: 'blur(16px) saturate(180%)',
                        WebkitBackdropFilter: 'blur(16px) saturate(180%)',
                    }}
                    boxShadow="none"
                    pointerEvents="auto"
                    width="auto"
                    maxW="calc(100% - 24px)"
                >
                    <HStack spacing={2}>
                        <Tooltip label="Zoom Out" placement="top" hasArrow>
                            <IconButton
                                aria-label="Zoom Out"
                                icon={<Icon as={TbZoomOut as React.ElementType} boxSize={4} />}
                                size="sm"
                                variant="ghost"
                                color="gray.600"
                                _hover={{ bg: 'whiteAlpha.700' }}
                                onClick={onZoomOut}
                                isDisabled={!onZoomOut}
                            />
                        </Tooltip>
                        <Tooltip label="Zoom In" placement="top" hasArrow>
                            <IconButton
                                aria-label="Zoom In"
                                icon={<Icon as={TbZoomIn as React.ElementType} boxSize={4} />}
                                size="sm"
                                variant="ghost"
                                color="gray.600"
                                _hover={{ bg: 'whiteAlpha.700' }}
                                onClick={onZoomIn}
                                isDisabled={!onZoomIn}
                            />
                        </Tooltip>
                        <Tooltip label="Reset View" placement="top" hasArrow>
                            <IconButton
                                aria-label="Reset View"
                                icon={<Icon as={TbRefresh as React.ElementType} boxSize={4} />}
                                size="sm"
                                variant="ghost"
                                color="gray.600"
                                _hover={{ bg: 'whiteAlpha.700' }}
                                onClick={onZoomReset}
                                isDisabled={!onZoomReset}
                            />
                        </Tooltip>
                        <Tooltip label="Rotate 90°" placement="top" hasArrow>
                            <IconButton
                                aria-label="Rotate 90°"
                                icon={<Icon as={TbRotateClockwise as React.ElementType} boxSize={4} />}
                                size="sm"
                                variant="ghost"
                                color="gray.600"
                                _hover={{ bg: 'whiteAlpha.700' }}
                                onClick={onRotate}
                                isDisabled={!onRotate}
                            />
                        </Tooltip>
                    </HStack>
                    {pageEditActions?.visible && (
                        <HStack spacing={2} flexShrink={0}>
                            <Menu placement="top-end">
                                <MenuButton
                                    as={Button}
                                    size="sm"
                                    variant="outline"
                                    borderColor="gray.300"
                                    color="gray.700"
                                    bg="whiteAlpha.700"
                                    _hover={{ bg: 'whiteAlpha.900' }}
                                    _active={{ bg: 'whiteAlpha.900' }}
                                >
                                    Edit
                                </MenuButton>
                                <MenuList minW="160px" py={1}>
                                    <MenuItem fontSize="sm" onClick={pageEditActions.onAdjustImages}>
                                        Adjust Images
                                    </MenuItem>
                                    <MenuItem fontSize="sm" onClick={pageEditActions.onResetOrder}>
                                        Reset
                                    </MenuItem>
                                    <MenuItem fontSize="sm" color="red.500" onClick={pageEditActions.onRemoveAll}>
                                        Remove All
                                    </MenuItem>
                                </MenuList>
                            </Menu>
                            <Tooltip label="Re-run extraction on the current page order" placement="top" hasArrow>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    borderColor="gray.300"
                                    color="gray.700"
                                    bg="whiteAlpha.700"
                                    _hover={{ bg: 'whiteAlpha.900' }}
                                    _active={{ bg: 'whiteAlpha.900' }}
                                    _disabled={{ bg: 'whiteAlpha.500', color: 'gray.400', cursor: 'not-allowed' }}
                                    onClick={pageEditActions.onReprocess}
                                    isDisabled={!pageEditActions.hasPages}
                                    isLoading={pageEditActions.applyLoading}
                                >
                                    Re-process
                                </Button>
                            </Tooltip>
                        </HStack>
                    )}
                    {!!currentImageSrc && (
                        <Tooltip label="Download high-resolution PDF" placement="top" hasArrow>
                            <IconButton
                                aria-label="Download high-resolution PDF"
                                icon={<Icon as={TbDownload as React.ElementType} boxSize={4} />}
                                size="sm"
                                variant="ghost"
                                color="gray.600"
                                _hover={{ bg: 'whiteAlpha.700' }}
                                onClick={() => void handleDownloadPdf()}
                                isLoading={isDownloadingPdf}
                            />
                        </Tooltip>
                    )}
                </HStack>
                {onOpenTeaching ? (
                    <Tooltip
                        label={teachingOpen ? 'Close AI extraction hints' : 'Tell us if a value is wrong'}
                        placement="left"
                        hasArrow
                    >
                        <IconButton
                            aria-label={teachingOpen ? 'Close AI extraction hints' : 'Teach extraction'}
                            icon={<Icon as={TbSparkles as React.ElementType} boxSize={5} />}
                            position="absolute"
                            top={3}
                            right={3}
                            zIndex={3}
                            size="md"
                            borderRadius="xl"
                            border="1px solid"
                            borderColor={teachingOpen ? 'blue.300' : 'whiteAlpha.600'}
                            bg={teachingOpen ? 'blue.500' : 'rgba(255, 255, 255, 0.7)'}
                            color={teachingOpen ? 'white' : ACCENT}
                            sx={{
                                backdropFilter: 'blur(16px) saturate(180%)',
                                WebkitBackdropFilter: 'blur(16px) saturate(180%)',
                            }}
                            _hover={{
                                bg: teachingOpen ? 'blue.600' : 'whiteAlpha.900',
                            }}
                            onClick={onOpenTeaching}
                        />
                    </Tooltip>
                ) : null}
                {currentImageSrc ? (
                    isPdf ? (
                        <PdfZoomImage
                            ref={zoomImageRef}
                            src={currentImageSrc}
                            pageNumber={currentImagePage}
                            onPageClamp={onPageChange}
                            onPageCountChange={onPdfPageCountChange}
                            hideControls
                            imageHighlight={imageHighlight ?? undefined}
                            imageHighlightFullWidth={imageHighlightFullWidth}
                        />
                    ) : (
                        <ZoomImage
                            ref={zoomImageRef}
                            hideControls
                            src={currentImageSrc}
                            alt={`Invoice ${invoiceNumber ?? ''} - Page ${currentImagePage}`}
                            w="100%"
                            maxH="100%"
                            objectFit="contain"
                            imageHighlight={imageHighlight ?? undefined}
                            imageHighlightFullWidth={imageHighlightFullWidth}
                        />
                    )
                ) : (
                    <Flex h="100%" align="center" justify="center" border="2px dashed" borderColor="gray.300" borderRadius="md" m={4}>
                        <Text color="gray.400" fontSize="lg">Problem loading image</Text>
                    </Flex>
                )}
            </Box>

            {/* Thumbnail strip (view mode — hidden while editing page order) */}
            {!hideDefaultThumbnailStrip && !isPdf && allImages.length > 1 && (
                <Box px={3} py={2} flexShrink={0} bg="white" borderTop="1px solid" borderColor="gray.200" overflowX="auto">
                    <HStack spacing={2}>
                        {allImages.map((img, idx) => (
                            <Box
                                key={idx}
                                w="64px"
                                h="48px"
                                flexShrink={0}
                                borderRadius="md"
                                overflow="hidden"
                                border="2px solid"
                                borderColor={currentImagePage === idx + 1 ? ACCENT : 'gray.200'}
                                cursor="pointer"
                                _hover={{ borderColor: currentImagePage === idx + 1 ? ACCENT : 'gray.400' }}
                                onClick={() => onPageChange(idx + 1)}
                                position="relative"
                            >
                                <Image src={img} alt={`Page ${idx + 1}`} w="100%" h="100%" objectFit="cover" />
                                {currentImagePage === idx + 1 && (
                                    <Box position="absolute" bottom={0} left={0} right={0} h="2px" bg={ACCENT} />
                                )}
                            </Box>
                        ))}
                    </HStack>
                </Box>
            )}
            {!hideDefaultThumbnailStrip && isPdf && (pdfPageCount ?? 1) > 1 && (
                <Box px={3} py={2} flexShrink={0} bg="white" borderTop="1px solid" borderColor="gray.200" overflowX="auto">
                    <HStack spacing={2}>
                        {Array.from({ length: pdfPageCount ?? 1 }, (_, idx) => {
                            const page = idx + 1;
                            const thumb = pdfViewThumbUrls[idx];
                            return (
                                <Box
                                    key={page}
                                    w="64px"
                                    h="48px"
                                    flexShrink={0}
                                    borderRadius="md"
                                    overflow="hidden"
                                    border="2px solid"
                                    borderColor={currentImagePage === page ? ACCENT : 'gray.200'}
                                    cursor="pointer"
                                    _hover={{ borderColor: currentImagePage === page ? ACCENT : 'gray.400' }}
                                    onClick={() => onPageChange(page)}
                                    position="relative"
                                    bg="gray.100"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    {thumb ? (
                                        <Image src={thumb} alt={`Page ${page}`} w="100%" h="100%" objectFit="cover" />
                                    ) : (
                                        <Text fontSize="2xs" color="gray.500" fontWeight="medium">
                                            {page}
                                        </Text>
                                    )}
                                    {currentImagePage === page && (
                                        <Box position="absolute" bottom={0} left={0} right={0} h="2px" bg={ACCENT} />
                                    )}
                                </Box>
                            );
                        })}
                    </HStack>
                </Box>
            )}
            {pageReplacement?.show && (
                <Box
                    px={pageReplacement.editMode && !pageReplacement.panelExpanded ? 0 : 3}
                    py={pageReplacement.editMode && !pageReplacement.panelExpanded ? 0 : 2}
                    flexShrink={0}
                    bg="white"
                    borderTop="1px solid"
                    borderColor="gray.200"
                >
                    <input
                        ref={pdfAppendInputRef}
                        type="file"
                        accept="application/pdf,image/tiff,image/tif,.tif,.tiff"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                            const f = e.target.files?.[0];
                            e.target.value = '';
                            if (f) pageReplacement.onAddPdf(f);
                        }}
                    />
                    <input
                        ref={imageAppendInputRef}
                        type="file"
                        accept={pageReplacement.imageAddAccept ?? INVOICE_IMAGE_ADD_ACCEPT}
                        multiple
                        style={{ display: 'none' }}
                        onChange={(e) => {
                            const files = e.target.files ? Array.from(e.target.files) : [];
                            e.target.value = '';
                            if (files.length) pageReplacement.onAddImages(files);
                        }}
                    />
                    {pageReplacement.draftBootstrapping && !pageReplacement.editMode && (
                        <Text fontSize="xs" color="gray.600">
                            Loading invoice pages…
                        </Text>
                    )}
                    {pageReplacement.error && !pageReplacement.editMode && !pageReplacement.draftBootstrapping && (
                        <Text fontSize="xs" color="red.500">
                            {pageReplacement.error}
                        </Text>
                    )}
                    {pageReplacement.editMode && (
                        <>
                            {pageReplacement.panelExpanded ? (
                                <>
                                    <HStack justify="space-between" mb={2} align="center">
                                        <Text fontSize="xs" color="gray.600">Adjust images</Text>
                                        <IconButton
                                            aria-label="Collapse image order controls"
                                            size="xs"
                                            variant="ghost"
                                            icon={<Icon as={TbChevronDown as React.ElementType} boxSize={4} />}
                                            onClick={() => pageReplacement.onSetPanelExpanded(false)}
                                        />
                                    </HStack>
                                    <InvoicePageDraftStrip
                                        loading={pageReplacement.pdfThumbsLoading}
                                        thumbnailUrls={pageReplacement.thumbnailUrls}
                                        sortableIds={pageReplacement.sortableIds}
                                        selectedIndex0={pageReplacement.selectedIndex0}
                                        onSelectIndex={pageReplacement.onSelectIndex}
                                        onReorder={pageReplacement.onReorder}
                                        onRemoveAt={pageReplacement.onRemoveAt}
                                        onAddClick={() =>
                                            pageReplacement.isPdf
                                                ? pdfAppendInputRef.current?.click()
                                                : imageAppendInputRef.current?.click()
                                        }
                                        error={pageReplacement.error}
                                        onCancel={pageReplacement.onCancel}
                                        onStartOver={pageReplacement.onStartOver}
                                        onReprocess={pageReplacement.onReprocess}
                                        applyLoading={pageReplacement.applyLoading}
                                        hasPages={pageReplacement.hasPages}
                                    />
                                </>
                            ) : (
                                <HStack
                                    spacing={2}
                                    align="center"
                                    h="44px"
                                    px={3}
                                    borderBottom="1px solid"
                                    borderColor="gray.200"
                                    bg="white"
                                >
                                    <HStack
                                        spacing={2}
                                        overflowX="auto"
                                        flex="1"
                                        minW={0}
                                        sx={{
                                            scrollbarWidth: 'none',
                                            msOverflowStyle: 'none',
                                            '&::-webkit-scrollbar': { display: 'none' },
                                        }}
                                    >
                                        {pageReplacement.sortableIds.map((id, i) => {
                                            const isSelected = pageReplacement.selectedIndex0 === i;
                                            return (
                                                <Box
                                                    key={id}
                                                    as="button"
                                                    type="button"
                                                    onClick={() => pageReplacement.onSelectIndex(i)}
                                                    px={3}
                                                    py={1.5}
                                                    bg={isSelected ? 'white' : 'transparent'}
                                                    color={isSelected ? 'gray.900' : 'gray.600'}
                                                    fontSize="xs"
                                                    fontWeight={isSelected ? 'semibold' : 'medium'}
                                                    borderRadius="md"
                                                    position="relative"
                                                    cursor="pointer"
                                                    whiteSpace="nowrap"
                                                    flexShrink={0}
                                                    _hover={{ bg: 'gray.50' }}
                                                >
                                                    {`Page ${i + 1}`}
                                                    {isSelected && (
                                                        <Box
                                                            position="absolute"
                                                            bottom="-6px"
                                                            left={2}
                                                            right={2}
                                                            h="2px"
                                                            bg={ACCENT}
                                                            borderRadius="full"
                                                            zIndex={1}
                                                        />
                                                    )}
                                                </Box>
                                            );
                                        })}
                                        <HStack
                                            as="button"
                                            type="button"
                                            spacing={1.5}
                                            px={3}
                                            py={1.5}
                                            color="gray.500"
                                            fontSize="xs"
                                            fontWeight="medium"
                                            border="1px dashed"
                                            borderColor="gray.300"
                                            borderRadius="md"
                                            cursor="pointer"
                                            whiteSpace="nowrap"
                                            flexShrink={0}
                                            _hover={{ bg: 'gray.50', color: 'gray.700', borderColor: 'gray.400' }}
                                            onClick={() =>
                                                pageReplacement.isPdf
                                                    ? pdfAppendInputRef.current?.click()
                                                    : imageAppendInputRef.current?.click()
                                            }
                                        >
                                            <AddIcon boxSize={2.5} />
                                            <Text as="span" fontSize="xs">Add file</Text>
                                        </HStack>
                                    </HStack>
                                    <Tooltip label="Expand image order controls" hasArrow placement="top">
                                        <IconButton
                                            aria-label="Expand image order controls"
                                            size="xs"
                                            variant="outline"
                                            borderColor="gray.300"
                                            color="gray.600"
                                            bg="white"
                                            borderRadius="md"
                                            flexShrink={0}
                                            icon={<Icon as={TbChevronUp as React.ElementType} boxSize={3.5} />}
                                            onClick={() => pageReplacement.onSetPanelExpanded(true)}
                                        />
                                    </Tooltip>
                                </HStack>
                            )}
                        </>
                    )}
                </Box>
            )}
        </Flex>
    );
};

export default InvoiceImagePanel;
